import { type CategoryType, categoryTraversal } from '@/Utility/categories';
import SvgZnglistIcon_FILE from '@/assets/images/znglist/znglist-icon.svg?file';
import { useAppStore } from '@/inertia/AppStore';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { Link as TsrLink, useLocation, useSearch } from '@tanstack/react-router';
import { useMemo } from 'react';

import styles from './Chips.module.css';

type Props = {
  disabled?: boolean;
};

const Chips = ({ disabled }: Props) => {
  const znglistNode = useAppStore((state) => state.znglist);
  const categories = useAppStore((state) => state.categories);
  const pageType = useAppStore((state) => state.pageType);
  const hubs = useAppStore((state) => state.hubs);

  const searchQuery = useSearch({
    strict: false,
    select(state) {
      return {
        s: state?.s,
        categories: state?.categories,
      };
    },
  });

  const categoriesFromLocation = useMemo(() => {
    if (!searchQuery?.categories) return [];

    const catIds = searchQuery.categories.filter((c) => !!c);

    const catsFromIds = categoryTraversal(categories?.edges as unknown as CategoryType, (cat) => {
      if (catIds.indexOf(cat?.id ?? '') > -1) return cat;
    }).filter((item) => !!item);

    return catsFromIds;
  }, [categories, searchQuery?.categories]);

  const location = useLocation();

  const selectedHub = useMemo(() => {
    return hubs?.edges?.find((hub) => (hub?.node?.slug ? location.pathname.includes(hub?.node?.slug) : false));
  }, [hubs, location.pathname]);

  return (
    <>
      {!!searchQuery?.s && searchQuery?.s !== '' && (
        <div className={styles.chip}>
          {/* biome-ignore lint/a11y/useSemanticElements: <explanation> */}
          <a className={styles.text} href="#" role="button">
            <SearchIcon className={styles.muiIcon} />
            {searchQuery?.s}
          </a>
          <TsrLink
            to="/"
            search={({ s, c, ...prev }) => ({
              ...prev,
            })}
            className={styles.close}
            disabled={disabled}
          >
            <CloseIcon className={styles.muiIcon} />
          </TsrLink>
        </div>
      )}
      {/* categories */}
      {categoriesFromLocation?.length
        ? categoriesFromLocation?.map((cat, idx) => {
            return (
              <div className={styles.chip} key={`${cat?.id}_${idx}`}>
                {/* biome-ignore lint/a11y/useSemanticElements: <explanation> */}
                <a className={styles.text} href="#" role="button">
                  {cat?.name}
                </a>
                <TsrLink
                  className={styles.close}
                  disabled={disabled}
                  to="/"
                  search={({ categories, c, ...prev }) => {
                    const cats = categories?.filter((category) => category !== cat.id) ?? [];

                    return {
                      ...prev,
                      ...(cats.length > 0 && { categories: cats }),
                    };
                  }}
                >
                  <CloseIcon className={styles.muiIcon} />
                </TsrLink>
              </div>
            );
          })
        : null}
      {!!znglistNode && pageType === 'znglist' && (
        <div className={styles.chip}>
          {/* biome-ignore lint/a11y/useSemanticElements: <explanation> */}
          <a className={styles.text} href="#" role="button">
            <span
              className={styles.icon}
              style={{
                maskImage: `url(\'${SvgZnglistIcon_FILE}\')`,
                WebkitMaskImage: `url(\'${SvgZnglistIcon_FILE}\')`,
              }}
            />
            {znglistNode?.title}
          </a>
          <TsrLink to="/" className={styles.close} disabled={disabled} search={{}}>
            <CloseIcon className={styles.muiIcon} />
          </TsrLink>
        </div>
      )}
      {!!selectedHub && (
        <div className={styles.chip}>
          {/* biome-ignore lint/a11y/useSemanticElements: <explanation> */}
          <a className={styles.text} href="#" role="button">
            <span
              className={styles.icon}
              style={{
                maskImage: `url(\'${SvgZnglistIcon_FILE}\')`,
                WebkitMaskImage: `url(\'${SvgZnglistIcon_FILE}\')`,
              }}
            />
            {decodeURIComponent(selectedHub?.node?.title ?? '')}
          </a>
          <TsrLink to="/" className={styles.close} disabled={disabled} search={{}}>
            <CloseIcon className={styles.muiIcon} />
          </TsrLink>
        </div>
      )}
    </>
  );
};

export const ExampleChip = () => {
  return (
    <div className={styles.chip}>
      <a className={styles.text}>
        <span
          className={styles.icon}
          style={{
            maskImage: `url(\'${SvgZnglistIcon_FILE}\')`,
            WebkitMaskImage: `url(\'${SvgZnglistIcon_FILE}\')`,
          }}
        />
        Chip
      </a>
      <span className={styles.close}>
        <CloseIcon className={styles.muiIcon} />
      </span>
    </div>
  );
};

export default Chips;
